import type { QueryClient } from '@tanstack/react-query'

import type { BookingQuotation, PaginatedResponse } from '@/types'

export type QuotationActionKind = 'accept' | 'convert'
export type QuotationListPreset = 'all' | 'open-quotes' | 'needs-contract' | 'converted'

export const COMMERCE_QUOTATION_PRESET_QUERY_PARAM = 'preset'
export const SALES_DASHBOARD_QUOTATION_PRESET_QUERY_PARAM = 'quote_preset'
export const SALES_QUOTATION_FILTER_QUERY_PARAMS = {
  quoteScope: 'quote_scope',
  contractScope: 'contract_scope',
  bookingScope: 'booking_scope',
} as const

export const OPTIMISTIC_BOOKING_REFERENCE = 'Creating booking...'

export const QUOTATION_STATUS_BADGE_CLASS: Record<string, string> = {
  initiated: 'border-indigo-300 bg-indigo-100 text-indigo-700',
  draft: 'border-slate-300 bg-slate-100 text-slate-700',
  sent: 'border-sky-300 bg-sky-100 text-sky-700',
  accepted: 'border-emerald-300 bg-emerald-100 text-emerald-700',
  converted: 'border-violet-300 bg-violet-100 text-violet-700',
  cancelled: 'border-rose-300 bg-rose-100 text-rose-700',
  expired: 'border-amber-300 bg-amber-100 text-amber-800',
}

export const QUOTATION_CARD_CLASS: Record<string, string> = {
  initiated: 'border-indigo-200 bg-indigo-50/40',
  draft: 'border-slate-200',
  sent: 'border-sky-200 bg-sky-50/40',
  accepted: 'border-emerald-200 bg-emerald-50/40',
  converted: 'border-violet-200 bg-violet-50/40',
  cancelled: 'border-rose-200 bg-rose-50/40',
  expired: 'border-amber-200 bg-amber-50/40',
}

const QUOTATION_PRESET_THEME_CLASS: Record<QuotationListPreset, { active: string; inactive: string; countActive: string; countInactive: string }> = {
  all: {
    active: 'border-slate-900 bg-slate-900 text-white shadow-sm hover:bg-slate-800',
    inactive: 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50',
    countActive: 'bg-white/15 text-white',
    countInactive: 'bg-slate-100 text-slate-700',
  },
  'open-quotes': {
    active: 'border-sky-500 bg-sky-500 text-white shadow-sm hover:bg-sky-600',
    inactive: 'border-sky-200 bg-sky-50 text-sky-800 hover:border-sky-300 hover:bg-sky-100',
    countActive: 'bg-white/15 text-white',
    countInactive: 'bg-sky-100 text-sky-800',
  },
  'needs-contract': {
    active: 'border-amber-500 bg-amber-500 text-white shadow-sm hover:bg-amber-600',
    inactive: 'border-amber-200 bg-amber-50 text-amber-900 hover:border-amber-300 hover:bg-amber-100',
    countActive: 'bg-white/15 text-white',
    countInactive: 'bg-amber-100 text-amber-900',
  },
  converted: {
    active: 'border-emerald-500 bg-emerald-500 text-white shadow-sm hover:bg-emerald-600',
    inactive: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:border-emerald-300 hover:bg-emerald-100',
    countActive: 'bg-white/15 text-white',
    countInactive: 'bg-emerald-100 text-emerald-800',
  },
}

const QUOTATION_PRESET_SURFACE_CLASS: Record<QuotationListPreset, { card: string; panel: string }> = {
  all: {
    card: 'border-slate-200 bg-gradient-to-br from-slate-50/80 via-white to-white',
    panel: 'border-slate-200 bg-white/80',
  },
  'open-quotes': {
    card: 'border-sky-200 bg-gradient-to-br from-sky-50 via-white to-cyan-50/70',
    panel: 'border-sky-100 bg-white/85',
  },
  'needs-contract': {
    card: 'border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50/70',
    panel: 'border-amber-100 bg-white/85',
  },
  converted: {
    card: 'border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50/70',
    panel: 'border-emerald-100 bg-white/85',
  },
}

export function formatQuotationMoney(value: unknown) {
  const numeric = Number(value ?? 0)
  return Number.isFinite(numeric) ? numeric.toFixed(2) : '0.00'
}

export function normalizeQuotationPreset(value: string | null | undefined): QuotationListPreset {
  return value === 'open-quotes' || value === 'needs-contract' || value === 'converted' ? value : 'all'
}

export function buildCommerceQuotationPresetPath(preset: QuotationListPreset = 'all') {
  if (preset === 'all') {
    return '/services/quotations'
  }
  return `/services/quotations?${COMMERCE_QUOTATION_PRESET_QUERY_PARAM}=${preset}`
}

export function buildSalesDashboardPresetPath(preset: QuotationListPreset = 'all') {
  if (preset === 'all') {
    return '/sales/dashboard'
  }
  return `/sales/dashboard?${SALES_DASHBOARD_QUOTATION_PRESET_QUERY_PARAM}=${preset}`
}

export function getQuotationPresetChipClass(preset: QuotationListPreset, isActive: boolean) {
  const theme = QUOTATION_PRESET_THEME_CLASS[preset]
  return `h-9 rounded-full border px-3 text-sm font-medium shadow-sm transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 ${isActive ? theme.active : theme.inactive}`
}

export function getQuotationPresetCountPillClass(preset: QuotationListPreset, isActive: boolean) {
  const theme = QUOTATION_PRESET_THEME_CLASS[preset]
  return `inline-flex min-w-7 items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors duration-200 ${isActive ? theme.countActive : theme.countInactive}`
}

export function getQuotationPresetSidebarBadgeClass(preset: Extract<QuotationListPreset, 'open-quotes' | 'needs-contract'>, isLoading = false) {
  if (isLoading) {
    return 'ml-auto min-w-6 justify-center border-slate-200 bg-slate-100 px-1.5 text-[11px] text-slate-500'
  }

  if (preset === 'open-quotes') {
    return 'ml-auto min-w-6 justify-center border-sky-200 bg-sky-100 px-1.5 text-[11px] text-sky-800'
  }

  return 'ml-auto min-w-6 justify-center border-amber-200 bg-amber-100 px-1.5 text-[11px] text-amber-900'
}

export function getQuotationPresetSurfaceClass(preset: QuotationListPreset) {
  return QUOTATION_PRESET_SURFACE_CLASS[preset].card
}

export function getQuotationPresetFilterPanelClass(preset: QuotationListPreset) {
  return QUOTATION_PRESET_SURFACE_CLASS[preset].panel
}

export function getQuotationPresetKpiCardClass(preset: QuotationListPreset) {
  if (preset === 'all') {
    return 'border-slate-200 bg-white'
  }
  if (preset === 'open-quotes') {
    return 'border-sky-200 bg-gradient-to-br from-sky-50/80 via-white to-white'
  }
  if (preset === 'needs-contract') {
    return 'border-amber-200 bg-gradient-to-br from-amber-50/80 via-white to-white'
  }
  return 'border-emerald-200 bg-gradient-to-br from-emerald-50/80 via-white to-white'
}

export function getQuotationPresetKpiAccentClass(preset: QuotationListPreset) {
  if (preset === 'all') {
    return 'bg-slate-900'
  }
  if (preset === 'open-quotes') {
    return 'bg-sky-500'
  }
  if (preset === 'needs-contract') {
    return 'bg-amber-500'
  }
  return 'bg-emerald-500'
}

export function getQuotationBookingMetaClass(value: unknown) {
  const normalized = String(value ?? '').trim()

  if (normalized === OPTIMISTIC_BOOKING_REFERENCE) {
    return 'border-amber-200 bg-amber-50/80'
  }

  if (normalized) {
    return 'border-emerald-200 bg-emerald-50/80'
  }

  return 'border-slate-200 bg-slate-50/80'
}

export function getQuotationContractMetaClass(hasContract: boolean) {
  return hasContract ? 'border-sky-200 bg-sky-50/80' : 'border-amber-200 bg-amber-50/70'
}

export function getQuotationActionButtonClass(action: 'accept' | 'convert' | 'contract' | 'booking') {
  if (action === 'accept') {
    return 'border-sky-200 bg-sky-50 text-sky-800 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-sky-300 hover:bg-sky-100 hover:shadow-md active:translate-y-0'
  }
  if (action === 'convert') {
    return 'bg-emerald-500 text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-emerald-600 hover:shadow-md active:translate-y-0'
  }
  if (action === 'contract') {
    return 'border-amber-200 bg-amber-50 text-amber-900 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-amber-300 hover:bg-amber-100 hover:shadow-md active:translate-y-0'
  }
  return 'border-emerald-200 bg-emerald-50 text-emerald-800 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-100 hover:shadow-md active:translate-y-0'
}

export function getQuotationSignalBadgeClass(signal: 'open' | 'contract' | 'ready') {
  if (signal === 'open') {
    return 'border-sky-200 bg-sky-50 text-sky-800'
  }
  if (signal === 'contract') {
    return 'border-amber-200 bg-amber-50 text-amber-900'
  }
  return 'border-emerald-200 bg-emerald-50 text-emerald-800'
}

export function getQuotationQuickActionClass(tone: 'neutral' | 'open' | 'contract' | 'ready') {
  if (tone === 'open') {
    return 'border-sky-200 bg-sky-50 text-sky-800 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-sky-300 hover:bg-sky-100 hover:shadow-md active:translate-y-0'
  }
  if (tone === 'contract') {
    return 'border-amber-200 bg-amber-50 text-amber-900 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-amber-300 hover:bg-amber-100 hover:shadow-md active:translate-y-0'
  }
  if (tone === 'ready') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-800 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-100 hover:shadow-md active:translate-y-0'
  }
  return 'border-slate-200 bg-white text-slate-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50 hover:shadow-md active:translate-y-0'
}

export function filterQuotationsByPreset<T extends Pick<BookingQuotation, 'status' | 'order_reference' | 'effective_contract_document_url' | 'contract_document_url'>>(
  quotations: T[],
  preset: QuotationListPreset,
) {
  return quotations.filter((quotation) => {
    if (preset === 'all') {
      return true
    }
    if (preset === 'converted') {
      return quotation.status === 'converted' || hasRealBookingReference(quotation.order_reference)
    }
    if (preset === 'needs-contract') {
      return !quotation.effective_contract_document_url && !quotation.contract_document_url
    }
    return quotation.status === 'initiated' || quotation.status === 'draft' || quotation.status === 'sent' || quotation.status === 'accepted'
  })
}

export function buildQuotationPresetCounts<T extends Pick<BookingQuotation, 'status' | 'order_reference' | 'effective_contract_document_url' | 'contract_document_url'>>(
  quotations: T[],
) {
  return {
    all: quotations.length,
    'open-quotes': filterQuotationsByPreset(quotations, 'open-quotes').length,
    'needs-contract': filterQuotationsByPreset(quotations, 'needs-contract').length,
    converted: filterQuotationsByPreset(quotations, 'converted').length,
  } as const
}

export function hasRealBookingReference(value: unknown) {
  const normalized = String(value ?? '').trim()
  return normalized.length > 0 && normalized !== OPTIMISTIC_BOOKING_REFERENCE
}

export function buildOptimisticQuotationPatch(action: QuotationActionKind, timestamp = new Date().toISOString()): Partial<BookingQuotation> {
  if (action === 'accept') {
    return { status: 'accepted', accepted_at: timestamp }
  }
  return {
    status: 'converted',
    converted_at: timestamp,
    order_reference: OPTIMISTIC_BOOKING_REFERENCE,
  }
}

export type OptimisticQuotationSnapshot = {
  queryKey: readonly unknown[]
  previous: PaginatedResponse<BookingQuotation> | undefined
}

export function updateQuotationStatusCache(
  queryClient: QueryClient,
  quotationId: string | number,
  patch: Partial<BookingQuotation>,
  queryKeys: readonly (readonly unknown[])[],
): OptimisticQuotationSnapshot[] {
  const snapshots = queryKeys.map((queryKey) => ({
    queryKey,
    previous: queryClient.getQueryData<PaginatedResponse<BookingQuotation>>(queryKey),
  }))

  for (const { queryKey } of snapshots) {
    queryClient.setQueryData<PaginatedResponse<BookingQuotation>>(queryKey, (current) => {
      if (!current?.results) {
        return current
      }
      return {
        ...current,
        results: current.results.map((quotation) => (
          String(quotation.id) === String(quotationId)
            ? { ...quotation, ...patch }
            : quotation
        )),
      }
    })
  }

  return snapshots
}

export function restoreQuotationStatusCache(
  queryClient: QueryClient,
  snapshots: OptimisticQuotationSnapshot[] | undefined,
) {
  for (const snapshot of snapshots ?? []) {
    queryClient.setQueryData(snapshot.queryKey, snapshot.previous)
  }
}
